<template>
    <div class="my-4">
        <div class="flex flex-wrap">
            <div class="lg:w-1/12 w-full mb-2 flex">
                <router-link
                    to="/app/cash-outputs"
                    class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                >
                    <span>🏠</span>
                </router-link>
            </div>
            <div class="w-full lg:w-10/12 mb-2 text-center">
                <h1 class="text-3xl">
                    Registrar gastos
                    {{ selectedMerchant.merchantType | merchantInfo("type") }}
                    <strong class="text-theme-primary">
                        {{ selectedMerchant.name }}
                    </strong>
                </h1>
            </div>
            <div class="w-full lg:w-1/12 flex justify-center">
                <custom-tooltip border>
                    <app-button
                        icon
                        border
                        :active="enableHelp"
                        @click="enableHelp = !enableHelp"
                    >
                        ℹ️
                    </app-button>
                    <template #tooltip>
                        <span>
                            ¿ Necesitas ayuda para registrar factura ?
                        </span>
                    </template>
                </custom-tooltip>
            </div>
        </div>

        <div class="mt-3">
            <span>
                En esta sección, tienes la opción de registrar tus gastos, ya
                sea por servicios o la adquisición de inventario.
                {{
                    $enabledEcommerceOptions
                        ? "Además, puedes adjuntar archivos o imágenes como comprobantes de pago."
                        : ""
                }}
                Si deseas registrar el gasto como una deuda, simplemente activa
                la casilla correspondiente que indica "Registrar como deuda".
            </span>
        </div>

        <process-guide
            v-model="enableHelp"
            title="¿Cómo registrar un gasto?"
            :steps="helpSteps"
        >
            Si además necesitas registrar el inventario asociado a este gasto,
            ve a
            <a href="/app/inventories/new" target="_blank" rel="noopener">
                Inventarios > Registrar
            </a>
            .
        </process-guide>

        <div class="flex flex-wrap mt-3">
            <div
                class="my-2 w-full lg:w-10/12 flex lg:flex-row flex-col justify-around"
            >
                <custom-checkbox v-model="needDebt" warn>
                    <span> Registrar como pago pendiente </span>
                </custom-checkbox>
                <custom-checkbox
                    v-if="$enabledEcommerceOptions"
                    v-model="needFile"
                    warn
                >
                    <span> Adjuntar comprobante de pago </span>
                </custom-checkbox>
                <custom-checkbox v-model="needDecrement" warn>
                    <span>
                        Pago hecho con dinero de caja (Te decrementará el dinero
                        en caja)
                    </span>
                </custom-checkbox>
            </div>
            <div v-if="needDecrement" class="w-full mt-3">
                <payment-methods-checker
                    v-model="itemComponent.sourcePay"
                    mode="output"
                >
                    <template #title>
                        <span class="font-bold">Método de pago</span>
                    </template>
                </payment-methods-checker>
            </div>

            <div class="w-full md:w-1/2 mt-6 px-2">
                <input-money-component
                    v-model="itemComponent.value"
                    enabled
                    :message-error="
                        !isValidValue & introValidators.value
                            ? 'Valor No puede ser $0'
                            : ''
                    "
                    label="Valor"
                    @blur="onBlurValue"
                />
            </div>

            <div class="w-full md:w-1/2 mt-6 px-2">
                <custom-input
                    v-model="itemComponent.concept"
                    type="text"
                    name="concept"
                    placeholder="Pago Energia Enero"
                    label="Concepto (Opcional)"
                    class="w-full"
                    :error="
                        !isValidConcept & introValidators.concept
                            ? 'El cocepto debe tener mínimo un caracter'
                            : ''
                    "
                />
            </div>

            <div class="w-full md:w-1/2 mt-6 px-2">
                <supplier-selector
                    v-model="itemComponent.provider"
                    fill
                    allow-create
                />
            </div>

            <div v-if="needDebt" class="w-full md:w-1/2 mt-6 px-2">
                <input-money-component
                    v-model="itemComponent.valuePaid"
                    enabled
                    label="Valor Abonado"
                    :message-error="
                        !isValidValue & introValidators.value
                            ? 'Valor Abonado No puede ser $0 y debe ser menor o igual que Valor'
                            : ''
                    "
                    @blur="introValidators.valuePaid = true"
                />
            </div>

            <div v-if="needFile" class="w-full mt-8">
                <private-file-uploader v-model="receiptFile" auto-loader />
            </div>
        </div>

        <div class="flex justify-center z-0 my-4">
            <app-button @click="createCashOutputComponent">
                Registrar Gasto
            </app-button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { titleRegex } from "@/common/lib/regex";

import PaymentMethodsChecker from "@/components/orders/PaymentMethodsChecker.vue";
import SupplierSelector from "@/components/suppliers/SupplierSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import PrivateFileUploader from "@/components/utils/PrivateFileUploader.vue";
import ProcessGuide from "@/components/utils/ProcessGuide.vue";

export default {
    name: "NewCashOutput",
    components: {
        PrivateFileUploader,
        InputMoneyComponent,
        PaymentMethodsChecker,
        SupplierSelector,
        CustomInput,
        ProcessGuide
    },
    data: () => ({
        needDebt: false,
        needFile: false,
        enableHelp: false,
        needDecrement: false,
        receiptFile: { url: "", name: "" },
        itemComponent: {
            sourcePay: "cash",
            provider: { name: "", slug: "" },
            concept: "",
            value: 0,
            valuePaid: 0
        },
        introValidators: {
            value: false,
            valuePaid: false
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        helpSteps() {
            const steps = [
                "Ingresa el concepto del gasto, por ejemplo: pago recibo de luz.",
                "Ingresa el proveedor del servicio, por ejemplo: Empresa de energía."
            ];
            if (this.$enabledEcommerceOptions) {
                steps.push(
                    "Selecciona el archivo comprobante desde tu dispositivo y espera a que se cargue."
                );
            }
            steps.push('Haz clic en "Registrar gasto".');
            return steps;
        },
        isValidProvider() {
            return titleRegex.test(this.provider);
        },
        isValidConcept() {
            return titleRegex.test(this.concept);
        },
        isValidValue() {
            return this.itemComponent.value > 0;
        },
        isValidValuePaid() {
            return (
                this.itemComponent.valuePaid > 0 &&
                this.itemComponent.valuePaid <= this.itemComponent.value
            );
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission(
            "cashOutput:Create",
            "/app/cash-outputs",
            true
        );
    },
    methods: {
        ...mapActions("cashOutput", ["createCashOutput"]),
        async createCashOutputComponent() {
            if (!this.validateData()) {
                return;
            }
            this.$loading.show();
            try {
                const result = await this.createCashOutput({
                    value: this.itemComponent.value,
                    valuePaid: this.itemComponent.valuePaid,
                    concept: this.itemComponent.concept || "Sin Concepto",
                    provider:
                        this.itemComponent.provider.name ||
                        "Proveedor indefinido",
                    sourcePay: this.itemComponent.sourcePay,
                    merchantId: this.selectedMerchant.merchantId,
                    merchantType: this.selectedMerchant.merchantType,
                    ...(this.needFile
                        ? { receiptFile: this.receiptFile }
                        : null)
                });
                this.receiptFile = { url: "", name: "" };
                this.$loading.hide();
                this.$sound.play();

                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Registro Exitoso",
                    text: `Gasto creado con el id ${result.id}.`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de Gastos",
                    cancelButtonText: "Nueva gasto"
                });

                if (dialogResult.isConfirmed) {
                    this.$router.push({
                        path: `/app/cash-outputs/detail/${result.id}`,
                        query: {
                            merchantId: this.selectedMerchant.merchantId,
                            merchantType: this.selectedMerchant.merchantType
                        }
                    });
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/cash-outputs/");
                }
                if (dialogResult.isDismissed) {
                    this.resetOutput();
                }
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onBlurValue() {
            this.introValidators.value = true;
            if (!this.introValidators.valuePaid) {
                this.itemComponent.valuePaid = +this.itemComponent.value;
            }
        },
        validateData() {
            this.introValidators = {
                value: true,
                valuePaid: true
            };

            const toComplete = [];

            if (!this.isValidValue) {
                toComplete.push("Valor");
            }
            if (!this.isValidValuePaid) {
                toComplete.push("Valor Abonado");
            }

            if (this.needFile && !this.receiptFile.url) {
                toComplete.push("Archivo");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Gasto invalido",
                    text: `Revisa por favor los siguientes campos ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        resetOutput() {
            this.introValidators = {
                value: false,
                valuePaid: false
            };
            this.itemComponent = {
                provider: { name: "", slug: "" },
                concept: "",
                value: 0,
                valuePaid: 0
            };
            this.receiptFile = { url: "", name: "" };
        }
    }
};
</script>
